import React from "react";

import {ChangeSet} from "./ChangeSet";

import Refresh from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";

import Aggregation from "./aggregations/Aggregation";
import Calculation from "./calculations/Calculation";
import GroupBy from "./GroupBy";
import {ColumnDef} from "../ColumnDef";

import {AggregationFormRow} from "../../AggregationFormRow";
import {CalculationFormRow} from "../../CalculationFormRow";
import {GroupByFormRow} from "../../GroupByFormRow";

import {isEmpty} from "weed-js";
import {Button} from "@mui/material";


export class View {

    constructor({columnDefs, rows, setGeneration}) {
        /**
         * When the user chooses a 'group by', 'aggregation', or 'calculation', or `filter` it's numbered in the order
         * it was added. That number is retrieved by the `getRenderOrder()` method of the column definition. We apply
         * the state change to the table in that order, as the user adds a new one.
         *
         * `this.state` preserves the current state of the table. When the user removes a state change, we re-apply the
         * entire state from start to finish to the original rows (`this.rows`). This is because state changes
         * (aggregations) are not reversible.
         */

        // Rows here not only reflects the original rows, but also the values appended to the rows by both aggregations
        // and calculations. This allows us to continue referencing previous operations in subsequent operations.
        if (rows === null || rows === undefined) {
            throw new Error("Rows cannot be null or undefined. Please provide valid rows.");
        }
        this.id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15); // Unique ID for this instance
        this._rows = rows;

        this._setGeneration = setGeneration; // This is a function to set the generation of the view, used for re-rendering.
        this._generation = 0; // This is the generation of the view, used for re-rendering.
        this.columnDefs = columnDefs || []; // This will hold the column definitions.

        this._state = this.getEmptyState(); // This is the current state of the view.

        this.changeSets = [new ChangeSet()];

        this._onStateChange = (newChanges) => {}; // This is a callback that will be called when the state changes.
        this._onPendingStateChange = (newChanges) => {}; // This is a callback that will be called when a stage change is staged but not applied.
    }

    toJSON() {
        return {
            columnDefs: this.columnDefs.map(colDef => colDef.toJSON()),
            changeSets: this.changeSets.map(changeSet => changeSet.toJSON()),
        };
    }

    static fromJSON(json) {
        const columnDefs = json.columnDefs.map(colDefJson => {
            if (colDefJson.className === "Aggregation") {
                return Aggregation.fromJSON({...colDefJson});
            } else if (colDefJson.className === "Calculation") {
                return Calculation.fromJSON({...colDefJson});
            } else if (colDefJson.className === "GroupBy") {
                return GroupBy.fromJSON({...colDefJson});
            }
            return ColumnDef.fromJSON({...colDefJson});
        });
        const view = new View({
            columnDefs: columnDefs,
            rows: [],
            setGeneration: () => {},
        });
        view.changeSets = json.changeSets.map(changeSetJson => ChangeSet.fromJSON(changeSetJson, {
            GroupBy,
            Aggregation,
            Calculation,
            ...Aggregation.operations,
            ...Calculation.operations,
        }));
        console.log("Pending calculations: ", view.getPendingCalculations());
        view.id = json.id;
        console.log("Replaying view from JSON:", view);
        view.replay();
        return view;
    }

    setGeneration(setter) {
        this._setGeneration(setter);
        this._generation = setter(this._generation || 0); // Update the generation of the view.
    }

    getGeneration() {
        /**
         * Returns the current generation of the view, which is used to force re-rendering of the view when the state
         * changes.
         *
         * @returns {number} - The current generation of the view.
         */
        return this._generation;
    }

    getRows() {
        return this._rows || [];
    }

    getViewableRows() {
        return this.getState().rows;
    }

    setRows(rows) {
        /**
         * Sets the rows of the view. This will also update the state with the new rows.
         *
         * @param {Array} rows - The new rows to set.
         * @returns {View} - Returns the current view instance for chaining.
         */
        this._rows = rows;
        this.getState().rows = rows.map(row => row.copy()); // Update the state with the new rows.
        return this;
    }

    getCurrentChangeSet() {
        /**
         * Returns the current change set, which is the last one in the changeSets array.
         *
         * @returns {ChangeSet} - The current change set.
         */
        return this.changeSets[this.changeSets.length - 1];
    }

    getPreviousChangeSet() {
        /**
         * Returns the previous change set, which is the second last one in the changeSets array.
         *
         * @returns {ChangeSet} - The previous change set.
         */
        if (this.changeSets.length < 2) {
            return null; // No previous change set exists.
        }
        return this.changeSets[this.changeSets.length - 2];
    }

    getLastAggregationChangeSet() {
        /**
         * Returns the last change set that contains an aggregation.
         *
         * @returns {ChangeSet|null}
         * If no aggregation change set exists, returns null.
         * Otherwise, returns the last change set that contains an aggregation.
         * */
        for (let i = this.changeSets.length - 1; i >= 0; i--) {
            const changeSet = this.changeSets[i];
            for (const change of changeSet.getChanges()) {
                if (change instanceof Aggregation) {
                    return changeSet; // Return the first change set that contains an aggregation.
                }
            }
        }
        return null; // No aggregation change set exists.
    }

    commitChanges() {
        /**
         * This method commits the changes made in the current change set, applying all pending state changes to the
         * view and updating the state.
         */
        if (this.changeSets.length === 0) {
            return;
        }
        for (const changeSet of this.changeSets) {
            changeSet.commitAll();
        }
        this.changeSets.push(new ChangeSet()); // Start a new change set for future changes.
        return this;
    }

    addOnStateChangeCallback(callback) {
        this._onStateChange = callback;
        return this;
    }

    addOnPendingStateChangeCallback(callback) {
        this._onPendingStateChange = callback;
        return this;
    }

    onStateChange(newChanges) {
        /**
         * This method is called when the state changes. It updates the state and calls the callback.
         *
         * @param {Array} newChanges - The new state changes that have been applied.
         */
        if (this._onStateChange) {
            return this._onStateChange(newChanges);
        }
    }

    onPendingStateChange(newChanges) {
        /**
         * This method is called when a new state change is staged but not applied. It updates the pending state changes
         * and calls the callback.
         *
         * @param {Array} newChanges - The new state changes that have been staged.
         */
        if (this._onPendingStateChange) {
            return this._onPendingStateChange(newChanges);
        }
    }

    getEmptyState() {
        const freshColumnDefs = [];
        for (let i = 0; i < this.columnDefs.length; i++) {
            freshColumnDefs.push(this.columnDefs[i]); // Copy the original column definitions.
        }
        return {
            rows: this.getRows().map(row => row.copy()), // The original rows, which will be modified by aggregations and calculations.
            columnDefs: freshColumnDefs, // The original column definitions, always by reference.
            groupBys: [], // The group by columns that have been applied to the view.
        };
    }

    getActiveColumnDefs() {
        /**
         * This method returns the column definitions that are currently viewable in the table.
         *
         * A column is viewable if it is either
         *  1) one of the originally defined columns, or
         *  2) a calculated column
         *
         * **_AND_** no aggregations or group bys have been applied yet.
         *
         * If group bys/aggregations have been applied, then the viewable columns ONLY include the group bys and
         * aggregations as well as calculations from the last ChangeSet, only.
         *
         */
        if (this.getAppliedGroupBys().length > 0 && this.getAppliedAggregations().length > 0) {
            const groupBys = [];
            const aggregations = [];
            for (const groupBy of this.getAppliedGroupBys()) {
                groupBys.push(groupBy);
            }
            for (const aggregation of this.getAppliedAggregations()) {
                if (!(aggregation instanceof Aggregation)) {
                    throw new Error("Invalid aggregation type.");
                }
                aggregations.push(aggregation);
            }
            const finalCalculations = this.getCurrentChangeSet().getChanges().filter(change => (change instanceof Calculation));
            return groupBys.concat(aggregations).concat(finalCalculations).map((def, index) => {
                // def.setRenderOrder(index);
                return def;
            });
        }

        const activeDefs = [];
        for (let i = 0; i < this.columnDefs.length; i++) {
            activeDefs.push(this.columnDefs[i]); // Add the column definition to the active definitions.
        }
        const calculations = [];
        for (const change of this.getAppliedCalculations()) {
            calculations.push(change); // Add the calculation column to the active definitions.
        }
        return activeDefs.concat(calculations).map((def, index) => {
            // def.setRenderOrder(index);
            return def;
        });
    }

    getColumnOffset() {
        return this.getActiveColumnDefs().length + this.getPendingStateChanges().length;
    }

    getPendingStateChanges() {
        // return this.pendingStateChanges;
        const stagedChanges = [];
        for (const changeSet of this.changeSets) {
            if (!changeSet.isCommitted()) {
                for (const change of changeSet.getStagedChanges()) {
                    stagedChanges.push(change);
                }
            }
        }
        return stagedChanges;
    }

    getAppliedStateChanges() {
        /**
         * Returns the applied state changes, which are the changes that have been applied to the view.
         *
         * @returns {Array} - An array of applied state changes.
         */
        const appliedStateChanges = [];
        for (const changeSet of this.changeSets) {
            for (const change of changeSet.getCommittedChanges()) {
                appliedStateChanges.push(change);
            }
        }
        return appliedStateChanges;
    }

    getState() {
        /**
         * Returns the current state of the view, which includes the rows and the column definitions.
         *
         * @returns {Object} - The current state of the view.
         */
        return this._state;
    }

    setState(state) {
        return this._state = state;
    }

    getAllColumnDefs() {
        /**
         * This method returns all the original column definitions, plus any additional state changes that are not group
         * bys, since group bys are already included in the original column definitions.
         *
         * This is contrasted with getColumnDefs() which returns only the column definitions that are currently viewable
         * (i.e., those that are grouped, aggregated, or calculated and not hidden).
         */
        const additionalDefs = [];
        const appliedChanges = this.getAppliedStateChanges();
        for (let i = 0; i < appliedChanges.length; i++) {
            if (appliedChanges[i] instanceof GroupBy) {
                continue;
            }
            additionalDefs.push(appliedChanges[i]);
        }

        if (additionalDefs.length === 0) {
            return this.getActiveColumnDefs() || this.columnDefs;
        }

        const _columnDefs = [];
        for (let i = 0; i < this.columnDefs.length; i++) {
            _columnDefs.push(this.columnDefs[i]); // Copy the original column definitions.
        }
        return _columnDefs.concat(additionalDefs); // Concatenate the additional definitions.
    }

    addStateChange(change) {
        /**
         * Adds a new state change to the view. This can be a group by, aggregation, calculation, or filter.
         *
         * @param {Object} change - The state change to add.
         * @returns {View} - Returns the current view instance for chaining.
         */
        if (change instanceof Calculation) {
            // It's ok to tack calculations on to the end (setting the index to the last element) because they're
            // applied immediately, but for aggregations and group bys we need to set/reset the index at the time the
            // view is rendered because choosing more/fewer can change the index of the column.
            change.index = this.getColumnOffset();
            change.setRenderOrder(this.getColumnOffset());
        }

        // Set the render order based on the current length of stateChanges.
        // change.setRenderOrder(this.pendingStateChanges.length);

        if (change instanceof Calculation) {
            // Calculations are applied immediately, as they are not destructive.
            change.apply(this);
            this.getCurrentChangeSet().commit(change); // Add the change to the current change set.
            this.setGeneration(prev => prev + 1);
        } else {
            // Aggregations and group bys are added to the pending state changes to be applied in batches since they are
            // destructive.
            this.getCurrentChangeSet().stage(change); // Add the change to the current change set.
        }

        return this;
    }

    getAppliedChanges() {
        const applied = [];
        for (const changeSet of this.changeSets) {
            for (const change of changeSet.getCommittedChanges()) {
                applied.push(change);
            }
        }
        return applied;
    }

    getAppliedCalculations() {
        /**
         * Returns the calculations applied to the view.
         *
         * @returns {Array} - An array of calculations.
         */
        return this.getAppliedChanges().filter(change => (change instanceof Calculation));
    }

    getAppliedAggregations() {
        /**
         * Returns the aggregations applied to the view.
         *
         * @returns {Array} - An array of aggregations.
         */
        return this.getAppliedChanges().filter(change => (change instanceof Aggregation));
    }

    getAppliedGroupBys() {
        /**
         * Returns the group by columns applied to the view.
         *
         * @returns {Array} - An array of group by columns.
         */
        return this.getAppliedChanges().filter(change => (change instanceof GroupBy));
    }

    getPendingCalculations() {
        /**
         * Returns the calculations applied to the view.
         *
         * @returns {Array} - An array of calculations.
         */
        // return this.pendingStateChanges.filter(change => (change instanceof Calculation));
        return this.getPendingStateChanges().filter(change => (change instanceof Calculation));
    }

    getPendingAggregations() {
        /**
         * Returns the aggregations applied to the view.
         *
         * @returns {Array} - An array of aggregations.
         */
        return this.getPendingStateChanges().filter(change => (change instanceof Aggregation));
    }

    getPendingGroupBys() {
        /**
         * Returns the group by columns applied to the view.
         *
         * @returns {Array} - An array of group by columns.
         */
        return this.getPendingStateChanges().filter(change => (change instanceof GroupBy));
    }

    removePendingStateChange(change) {
        /**
         * Removes a state change from the view. This can be a group by, aggregation, calculation, or filter.
         *
         * @param {Object} change - The state change to remove.
         * @returns {View} - Returns the current view instance for chaining.
         */
        if (!change) {
            console.error("Change is a required parameter. Cannot remove a change without it.");
            throw new Error("No change provided to remove.");
        }
        change.getChangeSet().remove(change); // Remove the change from all change sets.
        this.setGeneration(prev => prev + 1);
        return this;
    }

    removeAppliedStateChange(change) {
        change.getChangeSet().remove(change); // Remove the change from the current change set.
        this.replay()
        this.setGeneration(prev => prev + 1);
    }

    replay() {
        console.log("Replay called on view.");
        const changeSets = this.reset();
        console.log("Reset yielded change sets: ", changeSets);
        // if (changeSets.length <= 1) {
        //     console.log("There are no change sets to replay.");
        //     return this; // If there are no change sets, return the current view.
        // }
        for (const changeSet of changeSets) {
            console.log("Replaying change set: ", changeSet);
            for (const change of changeSet.getChanges()) {
                console.log("Adding state change: ", change);
                this.addStateChange(change); // Reapply the changes to the current view.
            }
            console.log("Applying all.");
            this.applyAll();
        }
        return this;
    }

    hasChanges() {
        if (this.getPendingStateChanges().length > 0 || this.getAppliedStateChanges().length > 0) {
            return true; // There are pending or applied state changes.
        }
        return false;
    }

    canSave() {
        for (const changeSet of this.changeSets) {
            if (changeSet.getChanges().length > 0) {
                return true; // There are changes to save.
            }
        }
        return false;
    }

    reset() {
        /**
         * Resets the view to its initial state, clearing all pending and applied state changes.
         *
         * @returns {View} - Returns the current view instance for chaining.
         */
        if (!this.hasChanges()) {
            return [];
        }
        this.printState();

        const detachedChangeSets = this.changeSets;
        for (const changeSet of detachedChangeSets) {
            changeSet.rollback(); // Rollback all changes in the change set.
        }

        this.changeSets = [new ChangeSet()]; // Reset the change sets to a new empty change set.
        this.setState(this.getEmptyState()); // Reset the state to the empty state.

        this.columnDefs.forEach((colDef, index) => {
            colDef.index = index; // Reset the index of each column definition.
            colDef.setRenderOrder(index); // Reset the render order of each column definition.
            colDef.setChangeSet(null);
        });

        if (this.getRows()?.length > 0 && this.getViewableRows()[0].cells.length !== this.columnDefs.length) {
            console.error("Resetting view, but the rows do not match the column definitions. Rows length:", this.getRows()[0].cells.length, "Column definitions length:", this.columnDefs.length);
            throw new Error("Rows do not match column definitions after reset.");
        }

        return detachedChangeSets;
    }

    setRenderOrder() {
        if (isEmpty(this.getActiveColumnDefs())) {
            return;
        }
        this.getActiveColumnDefs().reduce((index, colDef) => {
            colDef.index = index; // Reindex the active column definitions.
            colDef.setRenderOrder(index);
            return index + 1;
        }, 0);
        return this;
    }

    printState(detachedChangeSets = []) {
        let stateString = "~~~~~~~~~~~~~~~~~~~~~ VIEW STATE~~~~~~~~~~~~~~~~~~~~~\n";
        stateString += `Active Defs: ${this.getActiveColumnDefs().map(colDef => `${colDef.constructor.name}:${colDef.displayName}`).join(", ")}\n`;
        stateString += `Static Defs: ${this.columnDefs.map(colDef => `${colDef.constructor.name}:${colDef.displayName}`).join(", ")}\n`;
        stateString += `Rows: ${this.getViewableRows().length}\n`;
        stateString += "State Changes:\n";
        stateString += `Pending: ${this.getPendingStateChanges().length}: aggregations:${this.getPendingAggregations().length} calculations:${this.getPendingCalculations().length} group-by:${this.getPendingGroupBys().length}\n`;
        stateString += `Applied: ${this.getAppliedStateChanges().length}: aggregations:${this.getAppliedAggregations().length} calculations:${this.getAppliedCalculations().length} group-by:${this.getAppliedGroupBys().length}\n`;
        if (detachedChangeSets.length > 0) {
            stateString += `Detached Change Sets: ${detachedChangeSets.length}\n`;
            for (const changeSet of detachedChangeSets) {
                stateString += `  Change Set: ${changeSet.getChanges().length} changes\n`;
                for (const change of changeSet.getChanges()) {
                    stateString += `    - ${change.displayName} (${change.constructor.name})\n`;
                }
            }
        }
        stateString += "~~~~~~~~~~~~~~~~~~~~~END OF VIEW STATE~~~~~~~~~~~~~~~~~~~~~\n";
        console.log(stateString);
    }

    applyAll() {
        // What happens when we have an orphaned GroupBy? It needs to be pushed back to the last change set with an
        // aggregation, then we need to replay up to that change set.
        if (isEmpty(this.getPendingAggregations()) || isEmpty(this.getPendingGroupBys())) {
            return this;
        }

        this.setState(this.getPendingAggregations().reduce((state, change) => {
            return change.stage(this);
        }, this.getState()));

        // Apply GroupBy last, as it is destructive and modifies the state in-place.
        GroupBy.apply(this);

        this.setRenderOrder(); // Set the render order for all columns after applying all changes.
        this.commitChanges();

        return this;
    }

    setStyle(style) {
        for (const changeSet of this.changeSets) {
            for (const change of changeSet.getStagedChanges()) {
                change.setStyle(style);
            }
            for (const change of changeSet.getCommittedChanges()) {
                change.setStyle(style);
            }
        }
        return this;
    }

    pushBackPendingGroupBys() {
        for (const groupBy of this.getPendingGroupBys()) {
            this.getLastAggregationChangeSet().stage(groupBy); // Stage the group by in the last change set.
            this.getLastAggregationChangeSet().commit(groupBy); // Commit the group by to the last change set.
        }
    }

    renderPendingAggregations() {
        return <div className="existing-view-column">
            {this.getPendingAggregations().map((agg) => {
                return agg.renderPending(this)
            })}
        </div>
    }

    renderPendingCalculations() {
        return <div className="existing-view-column">
            {this.getPendingCalculations().map((calc) => {
                return calc.renderPending(this)
            })}
        </div>
    }

    renderPendingGroupBys() {
        return <div className="existing-view-column">
            {this.getPendingGroupBys().map((groupBy) => {
                return groupBy.renderPending(this)
            })}
        </div>
    }

    renderAppliedAggregations() {
        return <div className="applied-view-column">
            {this.getAppliedAggregations().map((agg) => {
                return agg.renderApplied(this)
            })}
        </div>
    }

    renderAppliedCalculations() {
        return <div className="applied-view-column">
            {this.getAppliedCalculations().map((calc) => {
                return calc.renderApplied(this)
            })}
        </div>
    }

    renderAppliedGroupBys() {
        return <div className="applied-view-column">
            {this.getAppliedGroupBys().map((groupBy) => {
                return groupBy.renderApplied(this)
            })}
        </div>
    }

    getFormRowKey() {
        /**
         * Generates a unique key for the form row based on the current generation.
         * This is used to force re-rendering of the form row when the generation changes.
         *
         * @returns {string} - A unique key for the form row.
         */
        return `form-row-${this.getGeneration()}-${Math.random().toString(36).substring(2, 15)}`;
    }

    renderAggregationFormRow() {
        return <AggregationFormRow key={this.getFormRowKey()} view={this} />
    }

    renderCalculationFormRow() {
        return <CalculationFormRow key={this.getFormRowKey()} view={this} />
    }

    renderGroupByFormRow() {
        return <GroupByFormRow key={this.getFormRowKey()} view={this} />
    }

    renderRefreshButton() {
        const hasGroupBys = this.getAppliedGroupBys().length > 0 || this.getPendingGroupBys().length > 0;
        const hasAggregations = this.getAppliedAggregations().length > 0 || this.getPendingAggregations().length > 0;
        const willHaveGroupBys = this.getPendingGroupBys().length > 0;
        const willHaveAggregations = this.getPendingAggregations().length > 0;

        let disabled = true; // Default to disabled, unless there are pending changes.
        if ((hasGroupBys || willHaveGroupBys) && (hasAggregations || willHaveAggregations)) {
            if (this.getPendingStateChanges().length > 0) {
                disabled = false;
            }
        }

        return <Button
            variant="contained"
            className="canold-table-refresh-button"
            role="canold-table-refresh-button"
            disabled={disabled}
            onClick={() => {
                const nGroups = this.getPendingGroupBys().length;
                const nOps = this.getPendingStateChanges().length;
                if (nGroups > 0 && nOps === nGroups) {
                    // Orphaned group bys, need to be pushed back to the previous change set containing an aggregation,
                    // then replay up to that set.
                    this.pushBackPendingGroupBys();
                    this.replay();
                } else {
                    this.applyAll();
                }
                this.setGeneration(prev => {
                    return prev + 1
                })
            }}
        ><Refresh/> &nbsp; Update View</Button>
    }

    renderResetButton() {
        return <Button
            variant="outlined"
            className="canold-table-refresh-button"
            role="canold-table-reset-button"
            onClick={() => {
                this.reset(); // Returns change sets if we need them, but we don't here.
                this.setGeneration(prev => {
                    return prev + 1
                })
            }}
        ><ClearIcon/> &nbsp; Reset View</Button>
    }
}
